import { Extension } from '@tiptap/core';

const MAX_INDENT = 5;
const INDENT_STEP = 2;

const clampIndent = (value: number): number => Math.max(0, Math.min(MAX_INDENT, value));

const EM_IN_PX = 16;
const PX_PER_INDENT = 40;

const getIndentValue = (value: string | null | undefined): number => {
    if (!value) {
        return 0;
    }
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
};

const parseIndentFromStyle = (style: string | null | undefined): number => {
    if (!style) {
        return 0;
    }

    const match = style.match(/margin-left\s*:\s*([^;]+)/i);
    if (!match) {
        return 0;
    }

    const raw = match[1].trim().toLowerCase();
    if (raw.endsWith('ch')) {
        const value = Number.parseFloat(raw.replace('ch', ''));
        return Number.isFinite(value) ? Math.round(value / INDENT_STEP) : 0;
    }
    if (raw.endsWith('em')) {
        const value = Number.parseFloat(raw.replace('em', ''));
        const px = Number.isFinite(value) ? value * EM_IN_PX : 0;
        return px > 0 ? Math.round(px / PX_PER_INDENT) : 0;
    }
    if (raw.endsWith('px')) {
        const value = Number.parseFloat(raw.replace('px', ''));
        const px = Number.isFinite(value) ? value : 0;
        return px > 0 ? Math.round(px / PX_PER_INDENT) : 0;
    }

    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? Math.round(value / INDENT_STEP) : 0;
};

const parseIndentFromClass = (className: string | null | undefined): number => {
    if (!className) {
        return 0;
    }

    const match = className.match(/ck-indent-(\d+)/i);
    if (!match) {
        return 0;
    }

    const parsed = Number.parseInt(match[1], 10);
    return Number.isFinite(parsed) ? parsed : 0;
};

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        indent: {
            indent: () => ReturnType;
            outdent: () => ReturnType;
        };
    }
}

export const Indent = Extension.create({
    name: 'indent',

    addGlobalAttributes() {
        return [
            {
                types: ['paragraph', 'heading'],
                attributes: {
                    indent: {
                        default: 0,
                        parseHTML: (element) => {
                            const dataIndent = getIndentValue(element.getAttribute('data-indent'));
                            if (dataIndent) {
                                return dataIndent;
                            }

                            const classIndent = parseIndentFromClass(element.getAttribute('class'));
                            if (classIndent) {
                                return classIndent;
                            }

                            return parseIndentFromStyle(element.getAttribute('style'));
                        },
                        renderHTML: (attributes) => {
                            const indent = clampIndent(attributes['indent'] || 0);
                            if (!indent) {
                                return {};
                            }
                            const textAlign = attributes['textAlign'] as string | null | undefined;
                            const alignStyle = textAlign ? `text-align: ${textAlign};` : '';
                            return {
                                'data-indent': indent,
                                style: `margin-left: ${indent * 40}px;${alignStyle}`,
                            };
                        },
                    },
                },
            },
        ];
    },

    addCommands() {
        return {
            indent:
                () =>
                    ({ state, commands }) => {
                        const { selection } = state;
                        const node = selection.$from.parent;
                        const current = clampIndent(node.attrs['indent'] || 0);
                        return (
                            commands.updateAttributes('paragraph', { indent: clampIndent(current + 1) }) ||
                            commands.updateAttributes('heading', { indent: clampIndent(current + 1) })
                        );
                    },
            outdent:
                () =>
                    ({ state, commands }) => {
                        const { selection } = state;
                        const node = selection.$from.parent;
                        const current = clampIndent(node.attrs['indent'] || 0);
                        return (
                            commands.updateAttributes('paragraph', { indent: clampIndent(current - 1) }) ||
                            commands.updateAttributes('heading', { indent: clampIndent(current - 1) })
                        );
                    },
        };
    },
});
